import * as React from 'react';
import styled, { css } from 'styled-components';
import { useCallback } from 'react';

import type {
  EventProceduresAPIType,
  EventProcedureStepAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';
import { BootstrapModalConfirm, MenuItem, DeleteMenuItem } from 'components/bootstrap';
import { useDeleteEventProcedure } from 'security-app/hooks/useEventProceduresAPI';
import { IconButton, Icon, IfPermitted, HasOwnership } from 'components/common';
import { DropdownMenu } from 'security-app/components/common';
import { useModalContext } from 'common/contexts';
import EntityShareModal from 'components/permissions/EntityShareModal';

type Props = {
  eventProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>;
};

const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
  `,
);

function RowActionMenu({ eventProcedure }: Props) {
  const { setModal, setEntity } = useModalContext();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = React.useState(false);

  const { deleteEventProcedure } = useDeleteEventProcedure();

  const onEdit = () => {
    setEntity(eventProcedure.id);
    setModal('EDIT-EVENT-PROCEDURE');
  };

  const onDelete = async () => {
    await deleteEventProcedure({ id: eventProcedure.id });
    setShowDeleteModal(false);
  };

  const toggleEntityShareModal = useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);

  return (
    <Wrapper>
      <DropdownMenu
        trigger={<IconButton name="more_horiz" title="Toggle event procedure actions" />}
        position="bottom-end">
        <IfPermitted permissions={[`event_procedures:edit:${eventProcedure.id}`]}>
          <MenuItem onClick={onEdit}>
            <Icon name="edit_square" size="xs" /> Edit
          </MenuItem>
        </IfPermitted>
        <HasOwnership id={eventProcedure.id} type="event_procedure">
          {({ disabled: hasMissingPermissions }) => (
            <MenuItem disabled={hasMissingPermissions} onClick={toggleEntityShareModal}>
              <Icon name="person_add" size="xs" /> Share
            </MenuItem>
          )}
        </HasOwnership>
        <IfPermitted permissions={[`event_procedures:delete:${eventProcedure.id}`]}>
          <DeleteMenuItem onClick={() => setShowDeleteModal(true)}>
            <Icon name="delete" size="xs" /> Delete
          </DeleteMenuItem>
        </IfPermitted>
      </DropdownMenu>
      <BootstrapModalConfirm
        title="Are you sure you want to delete this event procedure?"
        onConfirm={onDelete}
        onCancel={() => setShowDeleteModal(false)}
        showModal={showDeleteModal}>
        <div>You are about to delete this event procedure: {eventProcedure.title}</div>
      </BootstrapModalConfirm>
      {showEntityShareModal && (
        <EntityShareModal
          entityId={eventProcedure.id}
          entityType="event_procedure"
          entityTypeTitle="Event Procedure"
          entityTitle={eventProcedure.title}
          showShareableEntityURL={false}
          description="Search for a User or Team to add as collaborator on this event procedure."
          onClose={toggleEntityShareModal}
        />
      )}
    </Wrapper>
  );
}

export default RowActionMenu;
